import { useApolloClient } from '@apollo/client/react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  InfiniteScrollDropdownProps,
  InfiniteScrollDropdownSelection,
} from '~/components/InfiniteScrollDropdown/InfiniteScrollDropdown';
import { AudiencePageQueryAudience } from '~/constants/graphqlTypes';
import { useExtendedTranslation } from '~/hooks/useExtendedTranslation';
import {
  AudienceCondition,
  AudiencesPageDocument,
  AudiencesPageQuery,
  OrganizationType,
  useAudiencesPageQuery,
} from '~/types/api.graphql';

import { DropdownItemProps } from '../Dropdown/Dropdown';
import { StyledInfiniteScrollDropdown } from './AudiencesDropdown.styles';

const PAGE_ITEMS_COUNT = 10;

export interface AudiencesDropdownProps
  extends Omit<InfiniteScrollDropdownProps<AudiencePageQueryAudience>, 'onSelectionChange'> {
  organizationType?: OrganizationType;
  isAudienceDisabled?: (audience: AudiencePageQueryAudience) => boolean;
  onSelectionChange?: (selection: InfiniteScrollDropdownSelection, audience: AudiencePageQueryAudience) => void;
  organizationId?: string;
  conditions?: AudienceCondition[];
  disableAutoSelectSingle?: boolean;
  // By default the query is org-scoped and skipped until an organizationId is provided
  // (original behavior). Set this to query audiences across all organizations (header selector).
  allowAllOrganizations?: boolean;
}

const AudiencesDropdown = (props: AudiencesDropdownProps) => {
  const {
    allowAllOrganizations,
    conditions,
    disableAutoSelectSingle,
    isAudienceDisabled,
    onSelectionChange,
    organizationId,
    selectedIndex: selectedIndexProp,
  } = props;
  const { tSpecific } = useExtendedTranslation();
  const apolloClient = useApolloClient();
  const [items, setItems] = useState<DropdownItemProps<AudiencePageQueryAudience>[]>([]);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [searchOrgInfiniteScroll, setSearchOrgInfiniteScroll] = useState('');
  const [selectedIndex, setSelectedIndex] = useState<number>(props.selectedIndex);

  const disabledKeys = useMemo(() => {
    return items.filter((item) => item.disabled).map((item) => item.key);
  }, [items]);

  const applyAudiencesPage = useCallback(
    (data: AudiencesPageQuery) => {
      // Guard against a stale/unscoped response leaking foreign-org audiences into a scoped list:
      // when an organizationId is set, only accept nodes that belong to it.
      const nodes = (data?.audiencesPage?.nodes || []).filter(
        (audience) => !organizationId || audience.organizationId === organizationId,
      );
      setItems((prevItems) => [
        ...prevItems,
        ...nodes.map((audience) => ({
          text: audience.name,
          value: audience,
          description: tSpecific('audience.overview.'.concat(String(audience.condition).toLowerCase())),
          key: audience.id,
          disabled: isAudienceDisabled ? isAudienceDisabled(audience) : false,
        })),
      ]);
      setHasMore(data?.audiencesPage?.pageInfo?.hasNextPage);
      setPage((prevPage: number) => prevPage + 1);
    },
    [tSpecific, isAudienceDisabled, organizationId],
  );

  const buildVariables = useCallback(
    (pageNumber: number) => ({
      pageNumber,
      pageItemsCount: PAGE_ITEMS_COUNT,
      searchQuery: searchOrgInfiniteScroll,
      ...(organizationId ? { organizationId } : {}),
      ...(conditions ? { conditions } : {}),
    }),
    [searchOrgInfiniteScroll, organizationId, conditions],
  );

  const { loading: loadingFirst, data: audiencesQueryData } = useAudiencesPageQuery({
    variables: buildVariables(page),
    fetchPolicy: 'network-only',
    skip: page > 1 || (!organizationId && !allowAllOrganizations),
  });

  useEffect(() => {
    if (audiencesQueryData) {
      applyAudiencesPage(audiencesQueryData);
    }
    // Run once per Apollo data object. applyAudiencesPage changes can re-append the same page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audiencesQueryData]);

  useEffect(() => {
    setPage(1);
    setItems([]);
    setSelectedIndex(selectedIndexProp);
  }, [searchOrgInfiniteScroll, organizationId, selectedIndexProp]);

  const handleItemSelection = useCallback(
    (selection) => {
      const selectedIndex = selection as number;
      setSelectedIndex(selectedIndex);
      onSelectionChange?.(selectedIndex, items[selectedIndex].value);
    },
    [items, onSelectionChange],
  );

  // Keep a stable ref so the auto-select effect doesn't re-fire when the
  // onSelectionChange callback reference changes (e.g. on every parent render).
  const handleItemSelectionRef = useRef(handleItemSelection);
  handleItemSelectionRef.current = handleItemSelection;

  useEffect(() => {
    if (disableAutoSelectSingle) return;
    if (items?.length === 1 && !items?.[0]?.disabled) {
      handleItemSelectionRef.current(0);
    }
  }, [items, page, disableAutoSelectSingle]);

  const handleLoadMore = () => {
    // Guards against react-infinite-scroll-component firing `next` again before the in-flight
    // page finishes, which would also append that page twice.
    if (loadingMore) return;
    void (async () => {
      setLoadingMore(true);
      // A one-off `client.query` rather than the observable query's own `refetch`: refetching an
      // observable also updates its `data` (even while `skip: true`), which would re-fire the
      // effect above and append this same page a second time, the "same audience appearing
      // twice" bug when loading more pages in a search.
      const result = await apolloClient.query<AudiencesPageQuery>({
        query: AudiencesPageDocument,
        variables: buildVariables(page),
        fetchPolicy: 'network-only',
      });
      if (result.data) applyAudiencesPage(result.data);
      setLoadingMore(false);
    })();
  };

  return (
    <StyledInfiniteScrollDropdown
      {...props}
      items={items}
      disabledKeys={disabledKeys}
      onSelectionChange={handleItemSelection}
      selectedIndex={selectedIndex}
      setSearchOrgInfiniteScroll={setSearchOrgInfiniteScroll}
      searchOrgInfiniteScroll={searchOrgInfiniteScroll}
      hasMore={hasMore}
      loadMore={handleLoadMore}
      loadingFirst={loadingFirst}
      loadingMore={loadingMore}
      disallowEmptySelection
    />
  );
};

export default AudiencesDropdown;
